import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Building2, Briefcase, ChevronsUpDown, X, Link2, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { planGateFromResponse } from '../lib/planGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Searchable multi-select for linking Contacts to entities (Companies / Projects).
 * options: [{ id, name }], selected: string[] of ids, onChange(ids)
 */
export const EntityMultiSelect = ({ label, icon: Icon, options, selected, onChange, language, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const toggle = (id) =>
    selected.includes(id) ? onChange(selected.filter((x) => x !== id)) : onChange([...selected, id]);

  const filtered = options.filter((o) => o.name.toLowerCase().includes(q.toLowerCase()));
  const selectedOptions = options.filter((o) => selected.includes(o.id));

  return (
    <div className="space-y-2">
      {label && (
        <Label className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between bg-background font-normal"
            data-testid={testId}
          >
            <span className="truncate text-muted-foreground">
              {selected.length > 0
                ? `${selected.length} ${language === 'ar' ? 'محدد' : 'selected'}`
                : placeholder}
            </span>
            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="p-2 border-b border-border">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
              className="h-9 bg-background"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                {language === 'ar' ? 'لا توجد نتائج' : 'No results'}
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  onClick={() => toggle(o.id)}
                  className="flex items-center gap-2 w-full px-3 py-2 hover:bg-muted text-sm text-left"
                  data-testid={`${testId}-option-${o.id}`}
                >
                  <Checkbox checked={selected.includes(o.id)} className="pointer-events-none" />
                  <span className="truncate">{o.name}</span>
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedOptions.map((o) => (
            <Badge key={o.id} variant="secondary" className="gap-1 max-w-full">
              <span className="truncate">{o.name}</span>
              <X
                className="w-3 h-3 cursor-pointer shrink-0"
                onClick={() => toggle(o.id)}
                data-testid={`${testId}-remove-${o.id}`}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Dialog to manage a contact's Company & Project links (PUT /api/contacts/:id).
 */
export default function ContactLinksDialog({
  open,
  onOpenChange,
  contact,
  companies = [],
  projects = [],
  onSaved,
  token,
  language = 'en',
  isRTL = false,
  onPlanGate,
}) {
  const [companyIds, setCompanyIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && contact) {
      setCompanyIds(contact.linked_company_ids || contact.linkedCompanyIds || []);
      setProjectIds(contact.linked_project_ids || contact.linkedProjectIds || []);
    }
  }, [open, contact]);

  const handleSave = async () => {
    if (!contact?._id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/contacts/${contact._id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ linked_company_ids: companyIds, linked_project_ids: projectIds }),
      });
      if (!res.ok) {
        const pg = await planGateFromResponse(res);
        if (pg && onPlanGate) { onOpenChange(false); onPlanGate(pg); return; }
        throw new Error('Failed to update links');
      }
      const data = await res.json();
      toast.success(language === 'ar' ? 'تم تحديث الروابط' : 'Links updated');
      if (onSaved) onSaved(data.contact);
      onOpenChange(false);
    } catch (e) {
      toast.error(language === 'ar' ? 'فشل تحديث الروابط' : 'Failed to update links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link2 className="w-5 h-5 text-purple-500" />
            {language === 'ar' ? 'إدارة الروابط' : 'Manage Links'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {contact?.name && (
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              <span className="text-muted-foreground">
                {language === 'ar' ? 'جهة الاتصال: ' : 'Contact: '}
              </span>
              <span className="font-medium">{contact.name}</span>
            </div>
          )}
          <EntityMultiSelect
            label={language === 'ar' ? 'الشركات' : 'Companies'}
            icon={Building2}
            options={companies}
            selected={companyIds}
            onChange={setCompanyIds}
            language={language}
            placeholder={language === 'ar' ? 'اختر الشركات' : 'Select companies'}
            testId="links-companies-select"
          />
          <EntityMultiSelect
            label={language === 'ar' ? 'المشاريع' : 'Projects'}
            icon={Briefcase}
            options={projects}
            selected={projectIds}
            onChange={setProjectIds}
            language={language}
            placeholder={language === 'ar' ? 'اختر المشاريع' : 'Select projects'}
            testId="links-projects-select"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="links-cancel-btn">
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="links-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'ar' ? 'حفظ' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
